/* Lignon only supplies the initials UI. Dawn resolves options, prices and the cart. */
class LignonPersonalisation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const on = (node, type, callback, options = {}) => node?.addEventListener(type, callback, {...options, signal: this.controller.signal});
    this.root = this.closest('product-info');
    this.dialog = this.querySelector('dialog');
    this.letters = this.querySelector('[data-ln-initials]');
    this.entry = this.querySelector('[data-ln-entry]');
    this.messages = JSON.parse(this.querySelector('[data-ln-messages]').textContent);
    this.pending = false;
    on(this.querySelector('[data-ln-personalisation-open]'), 'click', () => this.open());
    on(this.querySelector('[data-ln-personalisation-close]'), 'click', () => this.dialog.close());
    on(this.entry, 'input', () => {
      this.entry.value = this.entry.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
      this.querySelector('[data-ln-error]').hidden = true;
      this.entry.removeAttribute('aria-invalid');
    });
    on(this.querySelector('[data-ln-save]'), 'click', () => this.save());
    on(this.entry, 'keydown', event => { if (event.key === 'Enter') { event.preventDefault(); this.save(); } });
    on(this.querySelector('[data-ln-remove]'), 'click', () => this.select(false));
    // Capture on product-info precedes Dawn's product-form listener.
    on(this.root, 'submit', event => {
      if (event.target.id !== this.dataset.form) return;
      const state = this.state();
      const selectedId = event.target.querySelector('[name="id"]')?.value;
      if (this.pending || !state?.configured || !state.validPair || selectedId !== state.variantId || (state.paid && !this.validLetters())) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.pending && state?.paid && state.validPair) this.open();
        else this.status(this.pending ? this.messages.pending : this.messages.unavailable);
      }
    }, {capture: true});
    on(this.root, 'change', event => {
      if (!event.target.closest('variant-selects')) return;
      this.pending = true;
      this.querySelector('[data-ln-personalisation-open]').disabled = true;
      this.status(this.messages.pending);
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => { if (this.pending) this.status(this.messages.failed); }, 12000);
    }, {capture: true});
    this.picker = this.root.querySelector('variant-selects');
    this.observer = new MutationObserver(() => {
      const picker = this.root.querySelector('variant-selects');
      if (picker !== this.picker) {
        this.picker = picker;
        this.pending = false;
        clearTimeout(this.timeout);
        this.refresh();
      }
    });
    this.observer.observe(this.root, {childList: true, subtree: true});
    this.refresh();
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); clearTimeout(this.timeout); }
  state() { try { return JSON.parse(this.root.querySelector('[data-ln-personalisation-state]')?.textContent || 'null'); } catch { return null; } }
  validLetters() { return /^[A-Z]{2}$/.test(this.letters.value); }
  status(message = '') { const node = this.querySelector('[data-ln-status]'); node.textContent = message; node.hidden = !message; }
  open() {
    if (this.pending) return;
    this.entry.value = this.letters.value;
    this.querySelector('[data-ln-error]').hidden = true;
    this.updatePreview();
    this.dialog.showModal();
    this.dialog.focus();
  }
  updatePreview() {
    const preview = this.state()?.preview;
    const figure = this.querySelector('[data-ln-preview]');
    const image = this.querySelector('[data-ln-preview-image]');
    if (!figure || !image) return;
    figure.hidden = !preview;
    this.dialog.classList.toggle('ln-personalisation-dialog--image', !!preview);
    if (!preview) {
      image.removeAttribute('srcset');
      image.removeAttribute('src');
      return;
    }
    image.alt = preview.alt;
    image.srcset = `${preview.small} 480w, ${preview.medium} 750w, ${preview.src} 1000w`;
    image.src = preview.src;
  }
  save() {
    if (!/^[A-Z]{2}$/.test(this.entry.value)) {
      this.querySelector('[data-ln-error]').hidden = false;
      this.entry.setAttribute('aria-invalid', 'true');
      this.entry.focus();
      return;
    }
    this.letters.value = this.entry.value;
    this.select(true);
  }
  select(paid) {
    const state = this.state();
    if (this.pending || !state?.configured || !state.validPair) return;
    const id = paid ? state.upgradeValueId : state.baseValueId;
    const input = [...this.root.querySelectorAll('[data-option-value-id]')].find(node => node.dataset.optionValueId === id);
    if (!input) { this.status(this.messages.unavailable); return; }
    if (state.paid === paid) { this.refresh(); this.dialog.close(); return; }
    if (input.tagName === 'OPTION') {
      input.parentElement.value = input.value;
      input.parentElement.dispatchEvent(new Event('change', {bubbles: true}));
    } else {
      input.checked = true;
      input.dispatchEvent(new Event('change', {bubbles: true}));
    }
    this.dialog.close();
  }
  refresh() {
    const state = this.state();
    if (this.dialog.open) this.updatePreview();
    const usable = state?.configured && state.validPair;
    this.querySelector('[data-ln-personalisation-open]').disabled = !usable;
    this.querySelector('[data-ln-remove]').disabled = !usable;
    this.querySelectorAll('[data-ln-surcharge]').forEach(node => { node.textContent = usable ? `+${state.surcharge}` : '—'; });
    if (state?.configured) {
      const input = [...this.root.querySelectorAll('[data-option-value-id]')].find(node => node.dataset.optionValueId === state.baseValueId);
      const group = input?.closest('fieldset, .product-form__input--dropdown');
      if (group) group.hidden = true;
    }
    this.letters.disabled = !usable || !state.paid || !this.validLetters();
    this.querySelector('[data-ln-summary]').hidden = !usable || !state.paid || !this.validLetters();
    this.querySelector('[data-ln-summary-text]').textContent = usable ? `${this.letters.value} · +${state.surcharge}` : '';
    this.status(!usable ? this.messages.unavailable : state.paid && !this.validLetters() ? this.messages.invalid : '');
    // Dawn owns availability; this component never enables a sold-out button.
    // Invalid/missing initials are caught before its submit listener, including deep links.
  }
}
if (!customElements.get('lignon-personalisation')) customElements.define('lignon-personalisation', LignonPersonalisation);
